import { ArrowLeftOutlined, InfoCircleFilled } from '@ant-design/icons';
import {
  useGetFrontRichiesteId,
  useGetFrontRichiesteIdModuli,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { EnumRichiestaModuloDTOStatoCompilazione } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaModuloDTOStatoCompilazione.ts';
import { useGetFrontTipiDomandaId } from '@framework/api-client/api/inquiries-compile/tipi-domanda/tipi-domanda.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { type ActionCanResult } from '@framework/common-web/fsm/EntityMachine.ts';
import { useCardinalityConstraintContext } from '@framework/common-web/schema/submissions/cardinality/CardinalityConstraintContext.tsx';
import { getCardinalityConstraintsViolations } from '@framework/common-web/schema/submissions/cardinality/getCardinalityConstraintsViolations.ts';
import { selectTranslatedInquiryType } from '@framework/common-web/schema/translations/selectTranslatedInquiryType.ts';
import { isInquiryReadonlyPreview } from '@framework/common-web/ui/inquiry/isInquiryReadonlyPreview.ts';
import { CompilationContainer } from '@framework/frontoffice-submission/burden/CompilationContainer.tsx';
import { SubmissionBurdenLogic } from '@framework/frontoffice-submission/burden/SubmissionBurdenLogic.ts';
import { DelegationMessageKey } from '@framework/frontoffice-submission/module/delegation/DelegationMachine.ts';
import { SubmissionCancelButton } from '@framework/frontoffice-submission/navigation/buttons/SubmissionCancelButton.tsx';
import { CardFooterButtons } from '@framework/frontoffice-submission/navigation/CardFooterButtons.tsx';
import { type ISubmissionNavigator } from '@framework/frontoffice-submission/navigation/useSubmissionNavigation.ts';
import { DownloadSubmissionModuleButton } from '@framework/frontoffice-submission/submission/download-pdf/DownloadSubmissionModuleButton.tsx';
import { SubmissionMenuKey } from '@framework/frontoffice-submission/submission/hooks/SubmissionMenuKey.ts';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { SubmissionSummaryLogic } from '@framework/frontoffice-submission/submission/signed-summary/SubmissionSummaryLogic.ts';
import { SubmissionSummarySigner } from '@framework/frontoffice-submission/submission/signed-summary/SubmissionSummarySigner.tsx';
import { ButtonResponsive } from '@framework/ui/components/button/ButtonResponsive.tsx';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { useIdFromUrl } from '@framework/ui/hooks/useIdFromUrl.ts';
import { Alert, Flex, Space } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import { NetworkAlertPage } from '../../../../routes/errors/network-alert/NetworkAlertPage.tsx';
import { LoadingPage } from '../../../../routes/loading/LoadingPage.tsx';
import { SubmissionSendButton } from './SubmissionSendButton.tsx';
import { SubmissionSummary } from './SubmissionSummary.tsx';
import { SubmissionValidationCard } from './SubmissionValidationCard.tsx';

interface Params {
  id: string;
}

export interface ICompilationSendCardProps extends RouteComponentProps<Params> {
  handleNavigation: ISubmissionNavigator;
  canSubmit: ActionCanResult;
}

/**
 * Schermata che gestisce l'invio della richiesta e la visualizzazione del riepilogo.
 */
export function CompilationSendCard(props: ICompilationSendCardProps) {
  const { handleNavigation, canSubmit } = props;
  const [submissionId] = useIdFromUrl(props, 'id');
  const { t } = useTranslation();
  const currentUser = useCurrentUser();

  const {
    data: submission,
    isLoading,
    error,
  } = useGetFrontRichiesteId(submissionId!, {
    query: { keepPreviousData: true, enabled: Boolean(submissionId) },
  });

  const { constraints } = useCardinalityConstraintContext();

  // biome-ignore lint/suspicious/noNonNullAssertedOptionalChain: <tbd>
  const inquiryRequest = useGetFrontTipiDomandaId(submission?.idTipoDomanda!, {
    query: {
      keepPreviousData: true,
      enabled: Boolean(submission?.idTipoDomanda),
      select: selectTranslatedInquiryType,
    },
  });

  const modulesRequest = useGetFrontRichiesteIdModuli(
    submissionId!,
    {},
    {
      query: {
        keepPreviousData: true,
        enabled: Boolean(submissionId),
      },
    },
  );

  const configurations = useMemo(
    () => inquiryRequest.data?.postCompilazione?.riepilogo,
    [inquiryRequest.data?.postCompilazione?.riepilogo],
  );

  const isCompleted = Boolean(
    modulesRequest.data?.every(
      m =>
        m.statoValidazione?.completato &&
        m.statoCompilazione ===
          EnumRichiestaModuloDTOStatoCompilazione.COMPLETATO,
    ),
  );

  const hasPendingPayments = SubmissionBurdenLogic.hasPendingPayments(
    submission?.statoOneri,
  );
  const hasAfterSendPayments = SubmissionBurdenLogic.hasAfterSendPayments(
    submission?.statoOneri,
  );

  const cannotSendAsDelegate = SubmissionLogic.cannotSendAsDelegate(
    submission,
    currentUser,
  );

  const showValidationCard = SubmissionSummaryLogic.isValidationRequired(
    inquiryRequest.data,
  );

  const showSummary = Boolean(
    configurations?.testo != null ||
      SubmissionBurdenLogic.hasBurdens(submission?.statoOneri) ||
      modulesRequest.data != null,
  );

  // Se c'+ più di un modulo, mostriamo il tasto di download per ogni modulo
  const hasInlineDownload =
    modulesRequest.data && modulesRequest.data.length > 1;

  // Se è disabilitato ma il message richiede il disabled, mostro comunque il pulsante
  const isSubmitDisabled =
    !canSubmit.allowed &&
    canSubmit.message === DelegationMessageKey.SUBMIT_BUTTON_DISABLED;

  // Nascondiamo il pulsante di invio se siamo in preview
  const showSubmitButton =
    !isInquiryReadonlyPreview(inquiryRequest?.data) &&
    (canSubmit.allowed || isSubmitDisabled);

  if (isLoading || modulesRequest.isLoading || inquiryRequest.isLoading) {
    return <LoadingPage noLayout />;
  }

  if (error ?? modulesRequest.error ?? inquiryRequest.error) {
    return (
      <NetworkAlertPage
        noLayout
        message={t(
          'submission-form.errore-in-caricamento-domanda',
          'Si è verificato un errore durante il caricamento della domanda.',
        )}
        error={error ?? modulesRequest.error ?? inquiryRequest.error}
      />
    );
  }

  if (!submissionId) {
    return (
      <NetworkAlertPage
        noLayout
        message={t(
          'submission-form.errore-in-caricamento-domanda',
          'Si è verificato un errore durante il caricamento della domanda.',
        )}
        error={t(
          'submission-form.id-della-richiesta-non-valido',
          'ID della richiesta non valido',
        )}
      />
    );
  }

  const previousText =
    configurations?.testoPulsanteIndietro ?? t('form.indietro', 'Indietro');

  return (
    <>
      <CompilationContainer>
        <Flex vertical gap={24}>
          {!isCompleted && canSubmit.allowed && (
            <Alert
              description={t(
                'submission-form.errore-moduli-incompleti-per-poter-procedere',
                "Alcuni moduli risultano incompleti o presentano errori. È necessario compilare tutti i moduli per procedere all'invio della richiesta.",
              )}
              message={t(
                'submission-form.compilazione-richiesta-incompleta',
                'Compilazione richiesta incompleta',
              )}
              showIcon
              type="warning"
            />
          )}
          {hasPendingPayments && (
            <Alert
              description={t(
                'submission-form.errore-oneri-non-pagati-per-poter-procedere',
                "Alcuni oneri risultano non pagati. È necessario pagare tutti gli oneri obbligatori per procedere all'invio della richiesta.",
              )}
              message={t(
                'submission-form.stato-pagamenti-incompleto',
                'Stato pagamenti incompleto',
              )}
              showIcon
              type="warning"
            />
          )}
          {hasAfterSendPayments && (
            <Alert
              message={t(
                'submission-form.inviare-per-procedere-al-pagamento',
                "Procedere all'invio della Richiesta per effettuare il pagamento degli oneri.",
              )}
              showIcon
              icon={<InfoCircleFilled />}
              type="error"
            />
          )}
          {showSummary && (
            <SubmissionSummary
              text={configurations?.testo}
              submission={submission}
              modules={modulesRequest.data}
              hasInlineDownload={hasInlineDownload}
              submissionId={submissionId}
              inquiryType={inquiryRequest.data}
              customizations={configurations?.personalizzazioni}
            />
          )}
          {showValidationCard && (
            <SubmissionValidationCard
              submissionId={submissionId}
              modules={modulesRequest.data}
            />
          )}
          <SubmissionSummarySigner
            submission={submission}
            inquiryType={inquiryRequest.data}
            isSubmissionComplete={isCompleted}
          />
        </Flex>
      </CompilationContainer>

      <CardFooterButtons>
        <FlexRow
          right={
            <Space>
              <SubmissionCancelButton />
              {showSummary &&
                !hasInlineDownload &&
                !SubmissionSummaryLogic.isSignedSummaryUploadEnabled() && (
                  <DownloadSubmissionModuleButton
                    submissionModuleId={modulesRequest.data?.[0]?.id}
                    submissionId={submissionId}
                    isModuleSigned={
                      modulesRequest.data?.[0]?.idFileFirmato != null
                    }
                    ignoreMobile
                    disabled={
                      SubmissionLogic.isSubmissionNew(submission) ||
                      !modulesRequest.data?.[0]?.statoValidazione?.visitato
                    }
                  />
                )}
              {/* Se è disabilitato ma il message richiede il disabled, mostro comunque il pulsante */}
              {showSubmitButton && (
                <SubmissionSendButton
                  submissionId={submissionId}
                  text={t('form.invia-la-domanda', 'Invia la domanda')}
                  isCompleted={isCompleted}
                  hasPendingPayments={hasPendingPayments}
                  cannotSendAsDelegate={cannotSendAsDelegate}
                  disabled={!canSubmit.allowed}
                  tooltip={
                    isSubmitDisabled
                      ? t(
                          'form.disable-submit-tooltip',
                          "L'invio è abilitato solo per il titolare o il procuratore.",
                        )
                      : undefined
                  }
                  cardinalityViolations={getCardinalityConstraintsViolations(
                    submission,
                    constraints,
                  )}
                />
              )}
            </Space>
          }
        >
          <ButtonResponsive
            size="large"
            iconLeftComponent={ArrowLeftOutlined}
            iconLeftAriaLabel={previousText}
            text={previousText}
            onClick={() => {
              handleNavigation.navigate(SubmissionMenuKey.Report, 'previous');
            }}
          />
        </FlexRow>
      </CardFooterButtons>
    </>
  );
}
